import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Task {
  id: string;
  companyId: string;
  companyName: string;
  comment: string;
  followUpDate: string;
  status: string;
}

interface CalendarProps {
  tasks?: Task[];
  onTaskCreated?: () => void;
}

export function Calendar({ tasks = [], onTaskCreated }: CalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<'month' | 'week' | 'day'>('month');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editedComment, setEditedComment] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const getWeekDates = () => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getTasksForDateRange = (start: Date, end: Date) => {
    return tasks.filter(task => {
      const taskDate = new Date(task.followUpDate);
      return taskDate >= start && 
             taskDate <= end && 
             task.status === 'pending';
    });
  };

  const monthNames = [
    'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'December'
  ];

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1;
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const days = Array.from({ length: daysInMonth(currentDate) }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDayOfMonth(currentDate) }, (_, i) => i);

  const getTasksForDate = (day: number) => {
    // Create a date object for the current day at midnight in local time
    const currentDateMidnight = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
      0, 0, 0
    );

    // Set the end of the day
    const endOfDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
      23, 59, 59
    );

    return tasks.filter(task => {
      const taskDate = new Date(task.followUpDate);
      return taskDate >= currentDateMidnight && 
             taskDate <= endOfDay && 
             task.status === 'pending';
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setEditedComment(task.comment);
    const taskDate = new Date(task.followUpDate);
    setEditedDate(taskDate.toISOString().slice(0, 16)); // Format: "YYYY-MM-DDThh:mm"
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!selectedTask) return;
    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          comment: editedComment,
          follow_up_date: new Date(editedDate).toISOString()
        })
        .eq('id', selectedTask.id);

      if (error) throw error;

      if (onTaskCreated) {
        onTaskCreated();
      }
    } catch (error) {
      console.error('Error updating task:', error);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
      setSelectedTask(null);
    }
  };

  const handleComplete = async () => {
    if (!selectedTask) return;
    setIsCompleting(true);

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          status: 'completed'
        })
        .eq('id', selectedTask.id);

      if (error) throw error;

      if (onTaskCreated) {
        onTaskCreated();
      }
    } catch (error) {
      console.error('Error completing task:', error);
    } finally {
      setIsCompleting(false);
      setSelectedTask(null);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-3">
        <h1 className="text-base font-bold">Kalender</h1> 
        <div className="flex items-center gap-2">
          <div className="bg-gray-100 rounded-lg p-0.5 flex">
            <button
              onClick={() => setViewType('month')}
              className={`px-2 py-1 text-xs rounded ${
                viewType === 'month'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Måned
            </button>
            <button
              onClick={() => setViewType('week')}
              className={`px-2 py-1 text-xs rounded ${
                viewType === 'week'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Uge
            </button>
            <button
              onClick={() => setViewType('day')}
              className={`px-2 py-1 text-xs rounded ${
                viewType === 'day'
                  ? 'bg-white shadow-sm text-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Dag
            </button>
          </div>
          <button
            onClick={goToToday}
            className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-xs"
          >
            I dag
          </button>
        </div>
      </div>
      <div className="bg-white rounded shadow-sm p-3">
        {viewType === 'month' ? (
          <>
            <div className="flex items-center justify-between mb-1">
              <button 
                onClick={previousMonth}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeft size={16} />
              </button>
              <h2 className="text-sm font-semibold">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button 
                onClick={nextMonth}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-px max-w-3xl mx-auto">
              <div className="text-center font-medium text-gray-500 text-xs">Ma</div>
              <div className="text-center font-medium text-gray-500 text-xs">Ti</div>
              <div className="text-center font-medium text-gray-500 text-xs">On</div>
              <div className="text-center font-medium text-gray-500 text-xs">To</div>
              <div className="text-center font-medium text-gray-500 text-xs">Fr</div>
              <div className="text-center font-medium text-gray-500 text-xs">Lø</div>
              <div className="text-center font-medium text-gray-500 text-xs">Sø</div>
              
              {blanks.map((blank) => (
                <div key={`blank-${blank}`} className="aspect-square p-1" />
              ))}
              
              {days.map((day) => {
                const isToday = 
                  day === new Date().getDate() && 
                  currentDate.getMonth() === new Date().getMonth() && 
                  currentDate.getFullYear() === new Date().getFullYear();

                const dayTasks = getTasksForDate(day);

                return (
                  <div
                    key={day}
                    className={`h-24 p-2 border border-gray-200 hover:bg-blue-50 cursor-pointer relative flex flex-col ${
                      isToday ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className={`text-sm ${isToday ? 'font-bold text-blue-600' : ''}`}>
                      {day}
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {dayTasks.map((task) => (
                        <div
                          key={task.id}
                          className="text-xs mb-1 p-1 bg-green-100 rounded truncate hover:bg-green-200 cursor-pointer"
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="font-medium truncate">{task.companyName}</div>
                          <div className="truncate text-gray-600">
                            {new Date(task.followUpDate).toLocaleTimeString('da-DK', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })} - {task.comment}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : viewType === 'week' ? (
          <div className="grid grid-cols-7 gap-px max-w-3xl mx-auto">
            {getWeekDates().map((date, index) => {
              const isToday = date.toDateString() === new Date().toDateString();
              const dayTasks = getTasksForDateRange(
                new Date(date.setHours(0, 0, 0, 0)),
                new Date(date.setHours(23, 59, 59, 999))
              );

              return (
                <div key={index} className="min-h-[150px]">
                  <div className={`text-[10px] font-medium p-1 border-b ${
                    isToday ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
                  }`}>
                    {date.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric' })}
                  </div>
                  <div className="p-1">
                    {dayTasks.map((task) => (
                      <div
                        key={task.id}
                        onClick={() => handleTaskClick(task)}
                        className="text-[9px] mb-1 p-1 bg-green-100 rounded cursor-pointer hover:bg-green-200"
                      >
                        <div className="font-medium truncate">{task.companyName}</div>
                        <div className="text-gray-600">
                          {new Date(task.followUpDate).toLocaleTimeString('da-DK', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                        <div className="truncate text-gray-600">{task.comment}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">
            Dagvisning kommer snart
          </div>
        )}
      </div>

      {selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded p-3 w-full max-w-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base font-bold">Opgavedetaljer</h2>
              <button
                onClick={() => {
                  setSelectedTask(null);
                  setIsEditing(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={16} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Virksomhed</label>
                <p className="text-base font-medium">{selectedTask.companyName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Opfølgningsdato</label>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-lg">{formatDate(selectedTask.followUpDate)}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <p className="text-lg">
                  <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                    selectedTask.status === 'pending' 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {selectedTask.status === 'pending' ? 'Afventer' : 'Afsluttet'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Kommentar</label>
                {isEditing ? (
                  <textarea
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                ) : (
                  <p className="text-lg">{selectedTask.comment}</p>
                )}
              </div>
            </div>
            <div className="flex justify-between mt-6">
              <button
                onClick={handleComplete}
                disabled={isCompleting || selectedTask.status === 'completed'}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle size={20} />
                {isCompleting ? 'Markerer som udført...' : 'Marker som udført'}
              </button>
              <div className="flex gap-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                      disabled={isSaving}
                    >
                      Annuller
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      disabled={isSaving}
                    >
                      {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setSelectedTask(null)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Luk
                    </button>
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Rediger
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}